// The season at a glance: every date the league plays, what is still free on each court, and
// whether a whole tie fits. See kumpula/TEAMS.md.
//
// This replaces the Excel some venues hand their teams - a date down the left, a row per court,
// the free hours in the cells - which works because the whole season is visible at once. The tie
// builder answers "is there room on THIS day"; this answers "where in the season is there room",
// and hands the date over to the builder to actually book.
//
// It needs `teams-seasonavailability`, which does not exist yet (TEAMS_BACKEND_CHANGES.md §3).
// Unlike the other backend asks there is no older behaviour to fall back to, so a failure says so
// plainly rather than rendering an empty list that looks like "the season is full".
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Alert, Badge, Spinner } from 'reactstrap'
import moment from 'moment'
import withAppconfig from '../../AppContext'
import TopNavigation from '../../TopNavigation'
import LottieLoading from '../../components/LottieLoading'
import { useManagerTeams } from '../../utils/useManagerTeams'
import { useTeamsApi } from '../../utils/teamsapi'
import { useCourts } from '../../utils/useCourts'
import { describeFormat } from '../../utils/teamformats'
import {
  teamContext,
  seasonDates,
  availabilityTable,
  seasonGridWindow,
  gridCells,
  formatDateLong,
  formatDateRange,
  momentLocale,
} from '../../utils/teamsview'
import { TeamsFailure } from './TeamsScreen'

// '17:00 - 22:00', in the wall-clock frame the rest of the feature uses.
const rangeLabel = (range) => {
  const from = moment.utc(range.start)
  const until = moment.utc(range.end)
  if (!from.isValid() || !until.isValid()) return ''
  return from.format('H:mm') + ' - ' + until.format('H:mm')
}

// The strips are the point of the screen: the Excel this replaces was readable because it was a
// grid, not because it listed times. White is bookable, grey is not - that is the whole
// vocabulary, so the cells stay flat and unbranded.
const GUTTER = 120
const CELL_BG = { free: '#fff', busy: '#c8c8c8', outside: 'transparent' }

const cellStyle = (state, col, last) => ({
  flex: '1 1 0',
  minWidth: 0,
  height: 14,
  background: CELL_BG[state] || CELL_BG.busy,
  // Darker on the hour, so the half-hour pairs read as hours. The last cell's right edge is the
  // container's own border.
  borderRight: last ? 'none' : '1px solid ' + (col.to % 60 === 0 ? '#333' : '#ccc'),
})

const Swatch = ({ state }) => (
  <span
    className='mr-1'
    style={{
      display: 'inline-block',
      width: 14,
      height: 14,
      verticalAlign: '-2px',
      border: '1px solid #333',
      background: CELL_BG[state],
    }}
  />
)

// The left gutter is repeated rather than shared so the axis, the name and the strip cannot
// drift apart: they are three separate flex rows, not a table.
const GridRow = ({ children }) => (
  <div className='d-flex'>
    <div style={{ flex: '0 0 ' + GUTTER + 'px', minWidth: GUTTER }} />
    <div style={{ flex: '1 1 0', minWidth: 0 }}>{children}</div>
  </div>
)

function TimeAxis({ columns }) {
  return (
    <div className='d-flex' style={{ fontSize: 10, lineHeight: '12px', paddingLeft: 1 }}>
      {columns.map((col) => (
        <div key={col.from} className='text-muted' style={{ flex: '1 1 0', minWidth: 0 }}>
          {col.label}
        </div>
      ))}
    </div>
  )
}

function CourtStrip({ court, columns }) {
  const cells = gridCells(court, columns)
  return (
    <div className='d-flex' style={{ border: '1px solid #333' }}>
      {columns.map((col, index) => (
        <div key={col.from} style={cellStyle(cells[index], col, index === columns.length - 1)} />
      ))}
    </div>
  )
}

function DayBlock({ entry, lang, grid, onPick }) {
  const { t } = useTranslation()
  const complete = entry.expected > 0 && entry.fits >= entry.expected
  return (
    <div className='border-bottom py-2'>
      <div className='d-flex justify-content-between align-items-center'>
        <strong>{formatDateLong(entry.date, lang)}</strong>
        <span>
          {entry.anyFree ? (
            <Badge color={complete ? 'success' : 'secondary'} className='mr-2'>
              {complete
                ? t('teams_season_fits_all')
                : t('teams_season_fits').replace('{FITS}', entry.fits).replace('{EXPECTED}', entry.expected)}
            </Badge>
          ) : (
            <Badge color='light' className='mr-2'>
              {t('teams_season_full')}
            </Badge>
          )}
          <Button size='sm' color='link' className='p-0' onClick={() => onPick(entry.date)}>
            {t('teams_season_book')}
          </Button>
        </span>
      </div>

      {grid ? (
        <GridRow>
          <TimeAxis columns={grid.columns} />
        </GridRow>
      ) : null}

      {/* One row per court, as in the Excel: a court with nothing free keeps its row. The times
          stay above the strip because the strip rounds to the half hour and the server does not
          snap free ranges to the clock - a 17:40 hole is only readable in the text. */}
      {entry.courts.map((court) => (
        <div key={court.resourceid} className='d-flex small mb-1'>
          <div style={{ flex: '0 0 ' + GUTTER + 'px', minWidth: GUTTER }} className='text-muted'>
            {court.name}
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className={court.ranges.length ? '' : 'text-muted'}>
              {court.ranges.length
                ? court.ranges.map(rangeLabel).filter((x) => x).join(', ')
                : t('teams_season_court_full')}
            </div>
            {grid ? <CourtStrip court={court} columns={grid.columns} /> : null}
          </div>
        </div>
      ))}
    </div>
  )
}

function TeamSeasonAvailabilityScreen({ appconfig }) {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { teamid } = useParams()
  const api = useTeamsApi({ appconfig })
  const { loading, data, failure } = useManagerTeams({ appconfig })

  const [dates, setDates] = useState(null)
  const [loadingDates, setLoadingDates] = useState(false)
  const [callFailure, setCallFailure] = useState(null)

  const { team, season, format } = teamContext(data, teamid)
  const courts = useCourts({ appconfig })
  const step = (appconfig.times && appconfig.times.i) || 30
  const today = moment().format('YYYY-MM-DD')

  useEffect(() => {
    if (!team) return undefined
    let alive = true
    setLoadingDates(true)
    api.seasonAvailability({ teamid }).then((res) => {
      if (!alive) return
      setLoadingDates(false)
      if (!res.ok) {
        setCallFailure(res)
        setDates(null)
        return
      }
      setCallFailure(null)
      setDates(Array.isArray(res.data.dates) ? res.data.dates : [])
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamid, !!team])

  if (loading) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container>
          <LottieLoading width={150} height={150} />
        </Container>
      </div>
    )
  }

  if (!team) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container>
          <TeamsFailure failure={failure} />
          {!failure ? <Alert color='secondary' className='mt-3'>{t('teams_not_found')}</Alert> : null}
        </Container>
      </div>
    )
  }

  const table = dates ? availabilityTable(dates, { courts, format, step }) : []
  // Computed once for the whole season, not per date: shared columns are what let a column be
  // scanned down the page.
  const grid = seasonGridWindow(table, step)
  // What the season *should* contain, so an empty answer can be told apart from a short one.
  const expectedDates = seasonDates(season, today)

  // Month headings: a 30-week season is 30-100 blocks and needs somewhere for the eye to land.
  let lastMonth = null

  return (
    <div className='full-page-background'>
      <TopNavigation />
      <Container>
        <Row>
          <Col xs='12'>
            <h4 className='mt-3'>{team.name}</h4>
            <div className='text-muted'>{season ? season.name : ''}</div>
            {season && formatDateRange(season.validstart, season.validend) ? (
              <div className='text-muted small'>
                {t('teams_season_active')} {formatDateRange(season.validstart, season.validend)}
              </div>
            ) : null}
            <div className='text-muted small mb-2'>{describeFormat(format)}</div>
            <Button
              color='link'
              className='pl-0'
              onClick={() => history.push(appconfig.pathPrefix + '/teams/' + teamid + '/season')}
            >
              {t('teams_back_to_season')}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xs='12' md='9'>
            <Card className='mb-4'>
              <CardHeader>{t('teams_season_availability_title')}</CardHeader>
              <CardBody>
                {/* The endpoint is not built yet, so say that rather than showing an empty
                    season, which would read as "everything is booked". */}
                {callFailure ? (
                  <Alert color='secondary'>
                    {callFailure.message || t('teams_season_unavailable')}
                  </Alert>
                ) : null}

                {loadingDates ? <Spinner color='primary' /> : null}

                {!loadingDates && !callFailure && table.length === 0 ? (
                  <Alert color='secondary'>{t('teams_season_no_dates')}</Alert>
                ) : null}

                {table.length ? (
                  <div className='text-muted small mb-2'>
                    {t('teams_season_count')
                      .replace('{COUNT}', table.length)
                      .replace('{FREE}', table.filter((e) => e.anyFree).length)}
                    {grid ? (
                      <span className='ml-3'>
                        <Swatch state='free' />
                        {t('teams_season_free')}
                        <span className='ml-3' />
                        <Swatch state='busy' />
                        {t('teams_season_busy')}
                      </span>
                    ) : null}
                  </div>
                ) : null}

                {table.map((entry) => {
                  const month = moment
                    .utc(entry.date, 'YYYY-MM-DD')
                    .locale(momentLocale(i18n.language))
                    .format('MMMM YYYY')
                  const heading = month !== lastMonth ? month : null
                  lastMonth = month
                  return (
                    <div key={entry.date}>
                      {heading ? (
                        <div className='mt-3 mb-1 font-weight-bold text-capitalize'>{heading}</div>
                      ) : null}
                      <DayBlock
                        entry={entry}
                        lang={i18n.language}
                        grid={grid}
                        onPick={(date) =>
                          history.push(
                            appconfig.pathPrefix + '/teams/' + teamid + '/tie?date=' + date
                          )
                        }
                      />
                    </div>
                  )
                })}

                {/* A short answer is worth flagging: the season has more playable dates than the
                    backend returned. */}
                {table.length && expectedDates.length > table.length ? (
                  <div className='text-muted small mt-3'>
                    {t('teams_season_partial')
                      .replace('{SHOWN}', table.length)
                      .replace('{TOTAL}', expectedDates.length)}
                  </div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default withAppconfig(TeamSeasonAvailabilityScreen)
